import { createContext, useCallback, useEffect, useState } from "react";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from "../hooks/useLocalePath";
import { getAccessToken, setSession, subscribeToSession } from "../apiClient";
import { logout as apiLogout, refresh } from "../httpService";

/**
 * Initial locale. The server render passes it in explicitly (there is no window there);
 * in the browser it comes from the /{locale}/... path prefix.
 */
function resolveInitialLocale(initialLocale) {
  if (SUPPORTED_LOCALES.includes(initialLocale)) return initialLocale;
  if (typeof window === "undefined") return DEFAULT_LOCALE;

  const first = window.location.pathname.split("/").filter(Boolean)[0];
  return SUPPORTED_LOCALES.includes(first) ? first : DEFAULT_LOCALE;
}

/** Where the old frontend kept the login token. Nothing is stored in localStorage any more. */
const legacyStorageKey = "HABWDAIWJB";

/** How long the page-load refresh waits before its one retry after a network error or a 5xx. */
const REFRESH_RETRY_DELAY_MS = 1000;

const noRoles = [];
// What a component sees without a provider above it: a visitor with no roles.
const UserContext = createContext({ roles: noRoles, hasRole: () => false });

const sameRoles = (a, b) => a.length === b.length && a.every((role, index) => role === b[index]);

/** A refresh that got no definitive answer: the network failed, or the API answered 5xx. */
const isTransientFailure = (failure) => failure?.status === 0 || failure?.status >= 500;

const UserContextProvider = ({ initialLocale, ...props }) => {
  const [userEmail, setUserEmail] = useState(null);
  const [roles, setRoles] = useState(noRoles);
  const [initialized, setInitialized] = useState(false);
  const [locale, setLocale] = useState(() => resolveInitialLocale(initialLocale));

  useEffect(() => {
    // The previous version stored the JWT here, readable by any script on the page. Drop it.
    localStorage.removeItem(legacyStorageKey);

    // Logins, silent refreshes and a failed refresh all report through apiClient, so the state
    // here follows the session however it changed.
    const unsubscribe = subscribeToSession((session) => {
      setUserEmail(session ? session.email : null);
      const nextRoles = session?.roles ?? noRoles;
      setRoles((current) => (sameRoles(current, nextRoles) ? current : nextRoles));
    });

    // The access token only lives in memory, so every page load starts by trading the httpOnly
    // refresh cookie for one. Without a session this resolves to null (the API answers 204) and the
    // visitor is simply anonymous; the subscriber above has already been told so via clearSession.
    // A network error or a 5xx (the API restarting during a deploy) says nothing about the cookie,
    // so it is tried once more before the visitor is treated as anonymous; `initialized` stays false
    // until then, which keeps RequireRole from bouncing an admin who reloaded at the wrong moment.
    let cancelled = false;
    refresh()
      .catch((failure) => {
        if (!isTransientFailure(failure)) return null;
        return new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS)).then(() => {
          // A login in the meantime has its own session; asking again could only disturb it.
          if (cancelled || getAccessToken()) return null;
          return refresh();
        });
      })
      .catch(() => {
        // The refresh failed twice: the visitor stays anonymous.
      })
      .finally(() => {
        if (!cancelled) setInitialized(true);
      });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  /** @param {{ jwt: string, email: string, roles: string[], verified: boolean, expiresIn: number }} authBody */
  const onLogin = useCallback((authBody) => {
    setSession(authBody);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // The local session is cleared regardless; the refresh cookie expires on its own.
    }
  }, []);

  const hasRole = useCallback((role) => roles.includes(role), [roles]);

  return (
    <UserContext.Provider
      value={{
        userEmail,
        roles,
        hasRole,
        onLogin,
        initialized,
        logout,
        locale,
        setLocale,
      }}
      {...props}
    />
  );
};

export { UserContextProvider, UserContext };
